import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Spacing, Text, MessageBox } from '@reservamos/elements';
import usePurchase from '../../hooks/store/usePurchase';
import PaymentImage from '../../ui/atoms/PaymentImage';
import CheckList from '../../ui/atoms/CheckList';
import './PaypalV6Form.scss';

/**
 * PaypalV6Form component.
 * Renders PayPal SDK v6 web components (paypal-button, paypal-pay-later-button, paypal-credit-button)
 * based on eligibility check results.
 * @param {Object} props - Component props with keys: onPaymentButtonClick: Function
 * @param {Function} props.onPaymentButtonClick - Function to handle payment button click with PayPal parameters
 * @returns {JSX.Element} PayPal V6 payment form with native web components.
 */
const PaypalV6Form = ({ onPaymentButtonClick }) => {
  const { t } = useTranslation('payment');
  const { paypalEligibility, isUpdating } = usePurchase();

  // Extract properties from paypalEligibility
  const { isLoading, isChecked, error, methods } = paypalEligibility || {};

  // Refs for web components
  const paypalButtonRef = useRef(null);
  const paylaterButtonRef = useRef(null);
  const creditButtonRef = useRef(null);

  // Setup event listeners for buttons when eligibility changes
  useEffect(() => {
    if (!paypalEligibility || isLoading) return;

    const handlers = [];

    // Setup PayPal standard button
    if (methods?.paypal?.isEligible && paypalButtonRef.current) {
      const button = paypalButtonRef.current;
      button.removeAttribute('hidden');

      /** Handles PayPal standard button click */
      const handleClick = () => {
        onPaymentButtonClick({
          paypalType: 'standard',
          paypalOptions: {},
        });
      };

      button.addEventListener('click', handleClick);
      handlers.push(() => {
        button.setAttribute('hidden', '');
        button.removeEventListener('click', handleClick);
      });
    }

    // Setup Pay Later button
    if (methods?.paylater?.isEligible && paylaterButtonRef.current) {
      const button = paylaterButtonRef.current;
      const paylaterMethod = methods.paylater;
      button.productCode = paylaterMethod.productCode;
      button.countryCode = paylaterMethod.countryCode;
      button.removeAttribute('hidden');

      /** Handles PayPal pay later button click */
      const handleClick = () => {
        onPaymentButtonClick({
          paypalType: 'paylater',
          paypalOptions: {
            productCode: paylaterMethod.productCode,
          },
        });
      };

      button.addEventListener('click', handleClick);
      handlers.push(() => {
        button.setAttribute('hidden', '');
        button.removeEventListener('click', handleClick);
      });
    }

    // Setup Credit button
    if (methods?.credit?.isEligible && creditButtonRef.current) {
      const button = creditButtonRef.current;
      const creditMethod = methods.credit;
      button.countryCode = creditMethod.countryCode;
      button.removeAttribute('hidden');

      /** Handles PayPal credit button click */
      const handleClick = () => {
        onPaymentButtonClick({
          paypalType: 'credit',
          paypalOptions: {},
        });
      };

      button.addEventListener('click', handleClick);
      handlers.push(() => {
        button.setAttribute('hidden', '');
        button.removeEventListener('click', handleClick);
      });
    }

    // Cleanup function
    return () => {
      handlers.forEach((cleanup) => cleanup());
    };
  }, [paypalEligibility, isLoading, methods, onPaymentButtonClick]);

  // Show error if eligibility check failed (prioritize error over loading)
  if (error && !isLoading) {
    return (
      <div className="checkout-section">
        <MessageBox type="error">
          <Text>{t('paypal_v6_status.paypal_eligibility_error')}</Text>
        </MessageBox>
      </div>
    );
  }

  // Show loading while checking eligibility OR while purchase is being updated
  if (!isChecked || isLoading || isUpdating) {
    return (
      <div className="checkout-section">
        <div className="paypal-eligibility-loading">
          <Text>
            {isUpdating
              ? t('paypal_v6_status.processing_payment')
              : t('paypal_v6_status.checking_payment_methods')}
          </Text>
        </div>
      </div>
    );
  }

  // Render PayPal official web components
  return (
    <div className="checkout-section">
      <Spacing vertical>
        <div className="checkout-paypal-logos">
          <PaymentImage type="paypal-logo" />
        </div>

        <CheckList
          title={t('label.pay_with_paypal_v6')}
          labels={[
            t('paypal_v6_instructions.first'),
            t('paypal_v6_instructions.second'),
            t('paypal_v6_instructions.third'),
            t('paypal_v6_instructions.fourth'),
          ]}
        />

        <div className="paypal-buttons-container">
          {/* Official PayPal SDK v6 web components */}
          <paypal-button id="paypal-button" ref={paypalButtonRef} type="pay" hidden />
          <paypal-pay-later-button id="paylater-button" ref={paylaterButtonRef} hidden />
          <paypal-credit-button id="paypal-credit-button" ref={creditButtonRef} hidden />
        </div>
      </Spacing>
    </div>
  );
};

PaypalV6Form.propTypes = {
  onPaymentButtonClick: PropTypes.func.isRequired,
};

export default PaypalV6Form;
